import React, { useState } from 'react';
import { Button, TextField, Box, Typography, Paper } from '@mui/material';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import i18n from '../i18n';
import { useLocalSettings } from '../contexts/LocalSettingsContext';
import { useTranslation } from 'react-i18next';

export default function Login({ onLogin }) {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { setSettings } = useLocalSettings();

  const API_KEY = process.env.REACT_APP_API_KEY;
  const API_SECRET = process.env.REACT_APP_API_SECRET;

  // Mapping utility for i18n language codes
  function mapLocaleToI18n(locale) {
    if (!locale) return 'fr';
    if (locale.startsWith('en')) return 'en';
    if (locale.startsWith('fr')) return 'fr';
    return 'fr'; // fallback
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
     // console.log('Login.js: handleSubmit called');
      const res = await fetch(`${api.defaults.baseURL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "x-api-key": API_KEY,
          "x-api-secret": API_SECRET
        },
        body: `username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`
      });
      if (!res.ok) throw new Error(t('login.errors.invalid'));
      const data = await res.json();
      //console.log('Login.js: user data', data);
      //console.log('Login.js: user data keys', Object.keys(data));
      //console.log('Login.js: user data (full)', data);
      // Stockage du token et du rôle comme dans archi
      window.sessionStorage.setItem('token', data.access_token);
      window.localStorage.setItem('token', data.access_token);
      window.sessionStorage.setItem('role', data.role);
      window.localStorage.setItem('role', data.role);
      // Save user with role to localStorage for global access
      if (data && data.role) {
        window.localStorage.setItem('currentUser', JSON.stringify(data));
      }
      window.localStorage.setItem('user', JSON.stringify({ username: data.username, role: data.role, uuid7: data.uuid7 || data.uuid || data.id }));

      // Correction : supporte aussi data.localgroup ou data.local_group_id ou data.localGroupId
      const group_id = data.local_group_id || data.localgroup || data.localGroupId || data.group_id || data.groupId;
      // Correction : supporte aussi data.appgroup ou data.app_group_id ou data.appGroupId
      const app_group_id = data.app_group_id || data.appgroup || data.appGroupId;
      window.localStorage.setItem('app_group_id', app_group_id || '');
      window.sessionStorage.setItem('app_group_id', app_group_id || '');
      //console.log('Login.js: group_id used for group-configs =', group_id);
      //console.log('Login.js: app_group_id used for app group apps =', app_group_id);
      // Récupération des paramètres locaux du groupe
      if (group_id) {
        //console.log('Login.js: group_id used for group-configs =', group_id);
        const groupRes = await fetch(`${api.defaults.baseURL}/api/config/group-configs/${group_id}`, {
          headers: {
            'x-api-key': API_KEY,
            'x-api-secret': API_SECRET,
            'Authorization': `Bearer ${data.access_token}`
          }
        });
        if (groupRes.ok) {
          const groupConfigs = await groupRes.json();
          //console.log('Login.js: groupConfigs =', groupConfigs);
          // Transforme la liste en objet clé/valeur
          const groupSettings = {};
          groupConfigs.forEach(cfg => {
            groupSettings[cfg.key.replace(/^group_\d+_local_/, '')] = cfg.value;
          });
          //console.log('Login.js: groupSettings =', groupSettings);
          setSettings(groupSettings);
          // Appliquer la langue immédiatement si présente
          if (groupSettings.displayLanguage) {
            const lang = mapLocaleToI18n(groupSettings.displayLanguage);
            i18n.changeLanguage(lang);
            //console.log('Login.js: set language to', lang, 'from', groupSettings.displayLanguage);
          }
        }
      }

      if (onLogin) onLogin(data);
      navigate("/desktop"); // Redirection après login réussi
    } catch (err) {
      console.error("Login.js: error during login flow", err);
      setError(t('login.errors.invalid'));
    }
  };

  return (
    <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
      <Paper elevation={3} sx={{ p: 6, minWidth: 340 }}>
        <Typography variant="h5" mb={2}>{t('login.title')}</Typography>
        <form onSubmit={handleSubmit}>
          <TextField 
            label={t('login.username')} 
            fullWidth 
            margin="normal" 
            value={username} 
            onChange={e => setUsername(e.target.value)} 
          />
          <TextField 
            label={t('login.password')} 
            type="password" 
            fullWidth 
            margin="normal" 
            value={password} 
            onChange={e => setPassword(e.target.value)} 
          />
          {error && <Typography color="error">{error}</Typography>}
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            fullWidth 
            sx={{ mt: 2 }}
          >
            {t('login.submit')}
          </Button>
        </form>
      </Paper>
    </Box>
  );
}
